import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { pessoaService } from '../services/pessoaService';

const PessoaAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Digite o nome ou CPF da pessoa...",
  required = false,
  className = "",
  disabled = false,
  dtype = "",
  textoVazio = "Nenhuma pessoa encontrada"
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Carregar pessoa selecionada se value estiver definido
  useEffect(() => {
    if (value && !selectedPerson) {
      loadPersonById(value);
    }
  }, [value, selectedPerson]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadPersonById = async (id) => {
    try {
      const result = await pessoaService.getById(id);
      if (result.success && result.data) {
        setSelectedPerson(result.data);
        setQuery(result.data.nome || '');
      }
    } catch (error) {
      console.error('Erro ao carregar pessoa:', error);
    }
  };

  const searchPeople = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const result = await pessoaService.search(searchQuery, { dtype });
      if (result.success) {
        setSuggestions(result.data);
      }
    } catch (error) {
      console.error('Erro ao buscar pessoas:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedPerson(null);
    
    // Se o input estiver vazio, limpar a seleção
    if (!newQuery) {
      onChange(null);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setShowSuggestions(true);
    
    // Debounce da busca
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      searchPeople(newQuery);
    }, 300);
  };

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setQuery(person.nome);
    setShowSuggestions(false);
    setSuggestions([]);
    onChange(person.idPessoa);
  };

  const handleClear = () => {
    setQuery('');
    setSelectedPerson(null);
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(null);
  };

  const formatPersonDisplay = (person) => {
    const parts = [];
    if (person.nome) parts.push(person.nome);
    if (person.cpf) parts.push(`CPF: ${person.cpf}`);
    if (person.email) parts.push(person.email);
    return parts.join(' - ');
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className="w-full min-h-11 border rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400" />
          ) : query ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          ) : (
            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Lista de sugestões */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((person) => (
            <button
              key={person.idPessoa}
              type="button"
              className="w-full min-h-11 px-3 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 border-b border-gray-100 last:border-b-0"
              onClick={() => handleSelectPerson(person)}
            >
              {/* A base tem cadastros com o nome em branco. Sem este recuo a
                  sugestao virava uma linha vazia, impossivel de interpretar. */}
              <div className={`font-medium ${person.nome ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                {person.nome || 'Cadastro sem nome'}
              </div>
              {/* Sem um identificador ao lado do nome, dois homônimos ficam
                  indistinguíveis na lista. A matrícula é o que a secretaria
                  usa no balcão; o tipo só aparece quando a busca não está
                  restrita a um. */}
              <div className="text-sm text-gray-600">
                {person.matricula && `Matrícula: ${person.matricula}`}
                {person.matricula && person.cpf && ' • '}
                {person.cpf && `CPF: ${person.cpf}`}
                {!dtype && person.dtype && ((person.matricula || person.cpf) ? ' • ' : '') + person.dtype}
                {!person.matricula && !person.cpf && !person.dtype && `código ${person.idPessoa}`}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Mensagem quando não há resultados */}
      {showSuggestions && !loading && query.length >= 2 && suggestions.length === 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-3 py-2 text-gray-500 text-sm">
            {textoVazio} para "{query}"
          </div>
        </div>
      )}

      {/* Campo hidden para o formulário */}
      <input
        type="hidden"
        name="pessoa"
        value={selectedPerson?.idPessoa || ''}
      />
    </div>
  );
};

export default PessoaAutocomplete;
